'use client';

import { FC, useContext, useState, useEffect, useCallback } from 'react';
import { ChangeLogDialog } from './_components/ChangeLogDialog';
import { Button } from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { getProductUpdates, NotionProductUpdate } from '@/_api/notion';
import styles from './ChangeLog.module.css';
import { User } from '@/_types/models/user';
import { updateChangelogLastReviewedAt } from '@/_api/users';

export const ChangeLog: FC = () => {
  const { user, token, setUser } = useContext(UserContext);

  const [isOpen, setIsOpen] = useState(false);
  const [hasNewUpdates, setHasNewUpdates] = useState(false);
  const [hasCalculatedStatus, setHasCalculatedStatus] = useState(false);
  const [changelogData, setChangelogData] = useState<NotionProductUpdate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasDialogBeenOpened, setHasDialogBeenOpened] = useState(false);
  const [capturedLastReviewedAt, setCapturedLastReviewedAt] = useState<
    string | null
  >(null);

  // Shared function to process changelog data and determine if there are new updates
  const processChangelogData = useCallback(
    (data: NotionProductUpdate[]) => {
      if (data.length === 0) return;

      // Filter for external items and find the most recent one by last_edited_time
      const externalItems = data.filter(
        (item) => item.properties.external_or_internal === 'External',
      );

      if (externalItems.length === 0) {
        // No external items, no new updates to show
        setHasNewUpdates(false);
        setHasCalculatedStatus(true);
        return;
      }

      const latestUpdate = externalItems.reduce((latest, current) => {
        const latestTime = new Date(latest.last_edited_time || 0).getTime();
        const currentTime = new Date(current.last_edited_time || 0).getTime();
        return currentTime > latestTime ? current : latest;
      });

      const latestUpdateTime = new Date(latestUpdate.last_edited_time || 0);

      // Handle date parsing more robustly
      let userLastReviewed = null;
      if (user?.change_log_last_reviewed_at) {
        try {
          const parsedDate = new Date(user.change_log_last_reviewed_at);
          if (!isNaN(parsedDate.getTime())) {
            userLastReviewed = parsedDate;
          } else {
            console.warn(
              'Invalid date format for change_log_last_reviewed_at:',
              user.change_log_last_reviewed_at,
            );
          }
        } catch (error) {
          console.warn('Error parsing change_log_last_reviewed_at:', error);
        }
      }

      // Show "new" indicator (pulsing) if the latest update is newer than user's last review
      let hasNew = false;

      if (!userLastReviewed) {
        // No previous review, show as new
        hasNew = true;
      } else if (userLastReviewed && !isNaN(userLastReviewed.getTime())) {
        // Valid date, compare timestamps
        hasNew = latestUpdateTime.getTime() > userLastReviewed.getTime();
      } else {
        // Invalid date format, but user has a timestamp, assume they've reviewed
        hasNew = false;
      }

      // Update the pulsing state after calculating the correct status
      setHasNewUpdates(hasNew);
      setHasCalculatedStatus(true);
    },
    [user],
  );

  const handleUpdateChangelogTimestamp = async () => {
    if (!token || !user?.id) return;
    try {
      const updatedUser = await updateChangelogLastReviewedAt({
        userId: user.id,
        token,
      });

      if (updatedUser) {
        setUser(updatedUser as User);
      }
    } catch (error) {
      console.error('Failed to update changelog review timestamp:', error);
    }
  };

  // Load changelog data when user logs in (single fetch)
  useEffect(() => {
    if (!token || !user || hasDialogBeenOpened) {
      return;
    }

    const loadChangelogData = async () => {
      try {
        setIsLoading(true);
        const productUpdates = await getProductUpdates({ token });
        const data = productUpdates.items;
        setChangelogData(data);

        // Process the data to determine if there are new updates
        processChangelogData(data);
      } catch (error) {
        console.error('Failed to load changelog data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadChangelogData();
  }, [token, user, hasDialogBeenOpened, processChangelogData]);

  // Manual refresh function
  const handleRefresh = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      const productUpdates = await getProductUpdates({ token });
      const data = productUpdates.items;
      setChangelogData(data);

      // Process the data to determine if there are new updates
      processChangelogData(data);
    } catch (error) {
      console.error('Failed to refresh changelog data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setCapturedLastReviewedAt(null);
    }
  }, [isOpen]);

  const onOpen = async () => {
    setIsOpen(true);
    setHasDialogBeenOpened(true);

    // Capture the timestamp BEFORE updating it
    setCapturedLastReviewedAt(user?.change_log_last_reviewed_at || null);

    // Fetch the latest data when dialog opens
    await handleRefresh();

    // Update the user's review timestamp immediately when dialog opens
    await handleUpdateChangelogTimestamp();

    // Immediately clear the new updates indicator when dialog opens
    setHasNewUpdates(false);
    setHasCalculatedStatus(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <ChangeLogDialog
        isOpen={isOpen}
        onClose={onClose}
        hasNewUpdates={hasNewUpdates}
        setHasNewUpdates={setHasNewUpdates}
        changelogData={changelogData}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        capturedLastReviewedAt={capturedLastReviewedAt}
      />
      <div className={styles.changelogContainer}>
        <Button
          value="What's New"
          intent="ghost"
          size="small"
          onClick={onOpen}
        />
        {hasCalculatedStatus && hasNewUpdates && (
          <div className={styles.newIndicator} />
        )}
      </div>
    </>
  );
};
